import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Languages, Users, RefreshCw, Award, Heart } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const WhyChooseUsSection = () => {
  const { t } = useLanguage();

  const reasons = [
    {
      icon: Languages,
      title: t('Bilingual, End-to-End', '全程双语服务'),
      description: t(
        'From the first phone call to bedside care, your family always speaks the same language as your carer.',
        '从第一通电话到床边护理，您的家人始终与护理员使用同一种语言。'
      )
    },
    {
      icon: ShieldCheck,
      title: t('Associated Provider & Insured', '关联服务商 · 已投保'),
      description: t(
        'We operate as an associated aged care provider in partnership with a registered provider, delivering services with full clinical governance and comprehensive insurance.',
        '我们作为关联养老服务商运营，与注册服务商合作，以完善的临床治理和综合保险提供服务。'
      )
    },
    {
      icon: Users,
      title: t('Same Team, Every Visit', '同一团队，每次上门'),
      description: t(
        'We assign a small, consistent team so trust and daily routines build naturally over time.',
        '我们安排一个小而稳定的团队，让信任和日常习惯自然建立。'
      )
    },
    {
      icon: Heart,
      title: t('Culturally Respectful', '尊重文化'),
      description: t(
        'From meals to festivals, we honour the rhythms and traditions that make home feel like home.',
        '从饮食到节日，我们尊重让家更有归属感的传统与习惯。'
      )
    },
    {
      icon: RefreshCw,
      title: t('Quarterly Care Reviews', '季度护理复审'),
      description: t(
        'Every care plan is formally reviewed each quarter so services flex with your changing needs.',
        '每份护理计划每季度正式复审一次，让服务随您需求的变化而调整。'
      )
    },
    {
      icon: Award,
      title: t('Trained, Audited Team', '受训且经审核的团队'),
      description: t(
        'Care Partners complete ongoing training and regular performance reviews for every shift.',
        '护理伙伴需完成持续培训和定期的绩效评估，覆盖每一次值班。'
      )
    }
  ];

  return (
    <section id="about" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-16">
          {/* Left: Header */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-sm font-semibold text-eucalyptus-600 tracking-wider uppercase mb-4"
              >
                {t('Why AU YH Care', '为何选择 AU YH Care')}
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-900 mb-6 tracking-tight leading-tight"
              >
                {t('Care rooted in', '扎根于')}
                <br />
                <span className="text-eucalyptus-600">{t('community', '社区的护理')}</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg text-gray-600 leading-relaxed"
              >
                {t(
                  'We are a Sydney-based home care provider built for Chinese families. Every family we support becomes part of ours.',
                  '我们是一家总部设在悉尼、为华人家庭打造的居家护理机构。我们服务的每一个家庭都成为我们大家庭的一员。'
                )}
              </motion.p>
            </div>
          </div>

          {/* Right: Grid */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {reasons.map((reason, index) => {
                const Icon = reason.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    className="group relative p-8 rounded-3xl border border-gray-100 hover:border-eucalyptus-200 hover:shadow-xl transition-all duration-500"
                    data-testid={`reason-${index}`}
                  >
                    <div className="absolute top-8 right-8 text-xs font-mono text-gray-300 group-hover:text-eucalyptus-400 transition-colors">
                      0{index + 1}
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-eucalyptus-50 group-hover:bg-eucalyptus-100 flex items-center justify-center mb-6 transition-colors">
                      <Icon className="w-6 h-6 text-eucalyptus-700" />
                    </div>
                    <h3 className="text-xl font-bold text-navy-900 mb-3 leading-tight">
                      {reason.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {reason.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
